import { useState } from 'react';
import { Check, X, Search } from 'lucide-react';
import { useLeaveRequests, useApproveLeave, useRejectLeave } from '@/api/queries/leave.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { cn } from '@/lib/cn';

const TABS = ['All','PENDING','APPROVED','REJECTED'];

export default function LeaveRequestsPage() {
  const [tab, setTab] = useState('All');
  const [search, setSearch] = useState('');
  const approve = useApproveLeave();
  const reject = useRejectLeave();

  const { data: resp } = useLeaveRequests({ status: tab === 'All' ? undefined : tab });
  const all = resp?.data || [];
  const requests = search ? all.filter(r => r.employee?.name?.toLowerCase().includes(search.toLowerCase())) : all;

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Leave Requests"
        subtitle={`${all.filter(r => r.status === 'PENDING').length} pending`}
        breadcrumb={[{ label: 'Leave', href: '/leave' }, { label: 'All Requests' }]}
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employee…"
            className="w-full pl-8 pr-3 py-2 text-[13px] border border-[var(--border)] rounded-[8px] bg-[var(--bg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"/>
        </div>
        <div className="flex gap-1.5">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={cn('px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-colors',
                tab === t ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-muted)] text-[var(--text-secondary)]')}>
              {t === 'All' ? `All (${all.length})` : t}
            </button>
          ))}
        </div>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-[var(--border)]">
            {['Employee','Type','Dates','Days','Reason','Status','Actions'].map(h => (
              <th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>
            ))}
          </tr></thead>
          <tbody className="divide-y divide-[var(--border)]">
            {requests.map(r => (
              <tr key={r.id} className="hover:bg-[var(--bg-muted)]">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <Avatar name={r.employee?.name} size="sm"/>
                    <div>
                      <p className="text-[13px] font-semibold text-[var(--text-primary)]">{r.employee?.name}</p>
                      <p className="text-[11px] text-[var(--text-tertiary)]">{r.employee?.department}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-[13px] font-medium text-[var(--text-primary)]">{r.leave_type}</td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{r.from_date} → {r.to_date}</td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{r.days}d</td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)] max-w-[160px] truncate">{r.reason || '—'}</td>
                <td className="px-5 py-3.5"><StatusBadge status={r.status}/></td>
                <td className="px-5 py-3.5">
                  {r.status === 'PENDING' && (
                    <div className="flex gap-2">
                      <button onClick={() => approve.mutate({ id: r.id })}
                        disabled={approve.isPending}
                        className="flex items-center gap-1 text-[11px] font-semibold text-green-600 hover:underline disabled:opacity-50">
                        <Check size={11}/> Approve
                      </button>
                      <button onClick={() => reject.mutate({ id: r.id, reason: '' })}
                        disabled={reject.isPending}
                        className="flex items-center gap-1 text-[11px] font-semibold text-red-500 hover:underline disabled:opacity-50">
                        <X size={11}/> Reject
                      </button>
                    </div>
                  )}
                  {r.status === 'APPROVED' && <span className="text-[11px] text-[var(--text-tertiary)]">by {r.approver || 'Manager'}</span>}
                  {r.status === 'REJECTED' && r.reject_reason && <span className="text-[11px] text-red-400" title={r.reject_reason}>Reason</span>}
                </td>
              </tr>
            ))}
            {requests.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">No leave requests</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
